/**
 * Web stand-in for `react-native-pell-rich-editor`.
 *
 * The native package drives a WebView-hosted pell editor. In a browser we can
 * use a contentEditable element directly, which is what pell itself does.
 *
 * ZenBasket POS currently reaches this module only through the
 * zen-core-elements barrel export (ZenEditor / ZenToolbar), so this shim exists
 * primarily to keep the web bundle free of WebView internals. It is a working
 * editor, not a stub, but it implements the subset of the API the wrapper
 * declares rather than every pell command.
 */

import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';
import {ScrollView, StyleSheet, Text, TouchableOpacity, View} from 'react-native';

export const actions = {
  setBold: 'bold',
  setItalic: 'italic',
  setUnderline: 'underline',
  setStrikethrough: 'strikeThrough',
  insertBulletsList: 'insertUnorderedList',
  insertOrderedList: 'insertOrderedList',
  alignLeft: 'justifyLeft',
  alignCenter: 'justifyCenter',
  alignRight: 'justifyRight',
  undo: 'undo',
  redo: 'redo',
  removeFormat: 'removeFormat',
  insertLink: 'createLink',
  heading1: 'heading1',
  heading2: 'heading2',
  heading3: 'heading3',
} as const;

const TOOLBAR_LABELS: Record<string, string> = {
  bold: 'B',
  italic: 'I',
  underline: 'U',
  strikeThrough: 'S',
  insertUnorderedList: '•',
  insertOrderedList: '1.',
  justifyLeft: '≡',
  justifyCenter: '≡',
  justifyRight: '≡',
  undo: '↶',
  redo: '↷',
  removeFormat: '✕',
};

export type RichEditorProps = {
  initialContentHTML?: string;
  placeholder?: string;
  disabled?: boolean;
  style?: any;
  editorStyle?: {backgroundColor?: string; color?: string; contentCSSText?: string};
  onChange?: (html: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  [key: string]: any;
};

export type RichToolbarProps = {
  getEditor?: () => any;
  editor?: any;
  actions?: string[];
  style?: any;
  [key: string]: any;
};

export type SelectionChangeListener = (items: string[]) => void;
export type FONT_SIZE = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export const RichEditor = forwardRef<any, RichEditorProps>(function RichEditor(props, ref) {
  const {initialContentHTML, placeholder, disabled, style, editorStyle, onChange, onFocus, onBlur} =
    props;
  const elementRef = useRef<HTMLDivElement | null>(null);
  const contentApplied = useRef(false);

  /*
   * Like the native editor, initialContentHTML is the *initial* content.
   * Screens pass it straight from the form value they update on every input,
   * so rendering it through dangerouslySetInnerHTML rewrote the editor on each
   * keystroke and threw the caret back to the start. Apply it on mount, and
   * afterwards only while the editor is not being typed in (e.g. a form reset).
   */
  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }
    const html = initialContentHTML ?? '';
    const editing = document.activeElement === element;
    if (!contentApplied.current || (!editing && element.innerHTML !== html)) {
      element.innerHTML = html;
      contentApplied.current = true;
    }
  }, [initialContentHTML]);

  const exec = useCallback((command: string, value?: string) => {
    const element = elementRef.current;
    if (!element) {
      return;
    }
    element.focus();
    // execCommand is deprecated but is still the only universally supported
    // way to run formatting against a contentEditable selection.
    document.execCommand(command, false, value);
  }, []);

  const emitChange = useCallback(() => {
    if (onChange && elementRef.current) {
      onChange(elementRef.current.innerHTML);
    }
  }, [onChange]);

  useImperativeHandle(
    ref,
    () => ({
      exec,
      getContentHtml: async () => (elementRef.current ? elementRef.current.innerHTML : ''),
      setContentHTML: (html: string) => {
        if (elementRef.current) {
          elementRef.current.innerHTML = html;
          emitChange();
        }
      },
      insertHTML: (html: string) => exec('insertHTML', html),
      insertText: (text: string) => exec('insertText', text),
      insertLink: (title: string, url: string) =>
        exec('insertHTML', `<a href="${url}">${title || url}</a>`),
      insertImage: (attributes: any) =>
        exec('insertHTML', `<img src="${attributes?.uri ?? attributes?.src ?? ''}" />`),
      setFontSize: (size: FONT_SIZE) => exec('fontSize', String(size)),
      setForeColor: (color: string) => exec('foreColor', color),
      setHiliteColor: (color: string) => exec('hiliteColor', color),
      focusContentEditor: () => elementRef.current?.focus(),
      blurContentEditor: () => elementRef.current?.blur(),
      dismissKeyboard: () => elementRef.current?.blur(),
      registerToolbar: () => {},
      setContentFocusHandler: () => {},
      commandDOM: () => {},
      command: (command: string) => exec(command),
      sendAction: (_type: string, action: string, data?: any) => exec(action, data),
      isKeyboardOpen: false,
    }),
    [emitChange, exec],
  );

  return (
    <View style={[styles.editorContainer, style]}>
      {React.createElement('div', {
        ref: elementRef,
        contentEditable: !disabled,
        suppressContentEditableWarning: true,
        'data-placeholder': placeholder,
        onInput: emitChange,
        onFocus,
        onBlur,
        style: {
          minHeight: 120,
          padding: 12,
          outline: 'none',
          overflowY: 'auto',
          backgroundColor: editorStyle?.backgroundColor ?? '#ffffff',
          color: editorStyle?.color ?? '#000000',
          fontFamily: 'inherit',
          fontSize: 15,
        },
      })}
    </View>
  );
});

export function RichToolbar(props: RichToolbarProps) {
  const {getEditor, editor, actions: toolbarActions, style} = props;
  const items = toolbarActions && toolbarActions.length ? toolbarActions : Object.values(actions);

  const run = (action: string) => {
    const target = (getEditor && getEditor()) || editor;
    const instance = target && target.current ? target.current : target;
    if (instance && typeof instance.exec === 'function') {
      instance.exec(action);
    }
  };

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={[styles.toolbar, style]}>
      {items
        .filter(action => TOOLBAR_LABELS[action])
        .map(action => (
          <TouchableOpacity key={action} style={styles.toolbarButton} onPress={() => run(action)}>
            <Text style={styles.toolbarLabel}>{TOOLBAR_LABELS[action]}</Text>
          </TouchableOpacity>
        ))}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  editorContainer: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 4,
    overflow: 'hidden',
  },
  toolbar: {
    flexGrow: 0,
    backgroundColor: '#f5f5f5',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  toolbarButton: {paddingHorizontal: 14, paddingVertical: 10},
  toolbarLabel: {fontSize: 16, color: '#333333'},
});

export default {RichEditor, RichToolbar, actions};
